import React from 'react';
import { TableHeader } from './TableHeader';
import { TableRow } from './TableRow';
import { Table, TableBody, TableContainer, TableCell, TableRow as MuiTableRow, Skeleton } from '@mui/material';
import { SxProps } from '@mui/system';


/*
 * Defining a Props interface that besides the CommonProps,
 * it will require an array of a object T that implements an id numeric key
 * or an array of a {id: number; value: T} interface,
 * in case the object T does not implement the id key.
 */
interface CommonProps<T> {
  title?: string;
  sx?: SxProps;
  enableCheck?: boolean;
  loading?: boolean;
  columns?: string[];
  columnsMap?: Record<string, string>;
  valuesMap?: Record<string, (value: any) => any>
  onRowClick?: (event: React.MouseEvent<unknown>, obj: T, key: React.Key) => void;
  onCheckboxClick?: (isChecked: boolean, row: T, key: React.Key) => void;
  onSelectAllClick?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
}

interface TableProps<T extends {}> extends CommonProps<T> {
  rows: Array<T>;
  selected?: Array<T>;
}

/**
 * `CustomTable` Provides a table based on an array of objects.
 *
 * It handles pagination automatically and also allows to
 * select the objects through click.
 * Also, the header columns are infered based on the keys of the object.
 * @param props
 * @returns
 */
export const CustomTable = <T extends {},>(props: TableProps<T>) => {
  /*
   * Component Variables
   */
  const {
    rows,
    selected,
    title,
    enableCheck = false,
    loading = false,
    sx,
    columns = [],
    columnsMap,
    valuesMap,
    onRowClick,
    onCheckboxClick,
    onSelectAllClick,
  } = props;

  /*
   * Component States
   */

  let rowCols = columns;
  if (rowCols.length === 0 && rows.length > 0) {
    rowCols = Object.keys(String(rows[0]));
  }

  let colHeader = rowCols;
  // Defining the Columns Names
  if (columnsMap != null) {
    colHeader = rowCols.map((c) => (c in columnsMap ? columnsMap[c] : c));
  }

  const isRowSelected = <T,>(obj: T): boolean => {
    if (selected !== undefined) {
      // @ts-ignore Builder keeps complaining for no reason
      return selected.findIndex((rowObj) => rowObj.id === obj.id) > -1 ? true : false;
    }
    return false;
  };

  return (
    <TableContainer sx={sx}>
      <Table aria-labelledby="tableTitle" size={'medium'} aria-label={title}>
        <TableHeader
          headers={colHeader}
          enableCheck={enableCheck}
          rowCount={rows.length}
          numSelected={selected && selected.length}
          onSelectAllClick={onSelectAllClick}
        />
        <TableBody  >
          {loading && (
            Array(4).fill('').map((_, i) => (
              <MuiTableRow key={i}>
                <TableCell  colSpan={columns.length}>
                  <Skeleton variant="rectangular"/>
                </TableCell>
              </MuiTableRow>
            )
          ))}

          {rows
            // // @ts-ignore
            // Handling Pagination locally
            // .slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage)
            // @ts-ignore
            .map((row, i) => {
              const isChecked = isRowSelected(row);
              const rowObj = row as any
              const key = rowObj?.id ? rowObj.id : i
              return (
                <TableRow<T>
                  key={key}
                  rowKey={key}
                  enableCheck={enableCheck}
                  row={row}
                  columns={rowCols}
                  valueMap={valuesMap}
                  isChecked={isChecked}
                  onRowClick={onRowClick}
                  onCheckboxClick={onCheckboxClick}
                />
              );
            })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
